import OfflineBanner from './OfflineBanner';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  IconButton, 
  Tooltip, 
  Menu, 
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Alert
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SettingsIcon from '@mui/icons-material/Settings';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import PaletteIcon from '@mui/icons-material/Palette';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { biometrics } from '../platform/biometrics';
import { logout } from '../store/authSlice';
import { useNavigate, Link } from 'react-router-dom';
import SessionTestBanner from './DevIndicator/SessionTestBanner';
import UnitDropdown from './UnitDropdown/UnitDropdown';
import { useThemeContext } from '../context/ThemeContext';
import BookmarkTutorial from './BookmarkTutorial/BookmarkTutorial';
import NotificationBell from './Notifications/NotificationBell';
import ThemeSettings from './Settings/ThemeSettings';
import { hasActivityAccess } from '../utils/activityAccess';
import { runtime } from '../platform/runtime';

const Header = ({ showAccountControls = true }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const signedEula = useSelector(state => state.auth.signedEula);
  const accountControlsVisible = showAccountControls && isAuthenticated && signedEula;
  const activeUnit = useSelector(state => state.auth.activeUnit);
  const activityEnabled = hasActivityAccess(activeUnit);
  const { toggleColorMode, mode } = useThemeContext();
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [themeSettingsOpen, setThemeSettingsOpen] = useState(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  
  const settingsMenuOpen = Boolean(settingsMenuAnchor);

  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    setLogoutError('');
    biometrics.suppressAutomaticLogin();
    try {
      await dispatch(logout({ explicitDeviceLogout: true }));
      setLogoutOpen(false);
      navigate('/login', { replace: true });
    } catch {
      setLogoutError('Could not finish removing saved sign-in from this device. Please try again.');
    } finally { setLoggingOut(false); }
  };

  const handleSettingsMenuOpen = (event) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const handleThemeToggle = () => {
    toggleColorMode();
    handleSettingsMenuClose();
  };

  const handleThemeSettings = () => {
    setThemeSettingsOpen(true);
    handleSettingsMenuClose();
  };

  const handleTutorialOpen = () => {
    setTutorialOpen(true);
    handleSettingsMenuClose();
  };

  const handleProfileNavigate = () => {
    navigate('/profile');
    handleSettingsMenuClose();
  };

  const handleLogoutFromMenu = () => {
    handleSettingsMenuClose();
    if (runtime.isNative()) setLogoutOpen(true);
    else {
      dispatch(logout());
      navigate('/login', { replace: true });
    }
  };  return (
    <>
      <AppBar position={runtime.isNative() ? 'sticky' : 'static'} sx={runtime.isNative() ? {
        paddingTop: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
        paddingLeft: 'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))',
        paddingRight: 'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))'
      } : undefined}>
        <OfflineBanner />
        <SessionTestBanner />
        <Toolbar>
          <Box 
            component={Link} 
            to="/dashboard" 
            sx={{ 
              flexGrow: 1, 
              minWidth: 0,
              textDecoration: 'none', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img 
              src="/icons/Connect App Logo v2 white.svg"
              alt="Connect App 365" 
              style={{ height: '40px', maxWidth: '100%', objectFit: 'contain' }}
            />
          </Box>

          {accountControlsVisible && activityEnabled && (
            <Tooltip title="Activity">
              <IconButton
                color="inherit"
                onClick={() => navigate('/activity')}
                aria-label="Open activity insights"
                sx={{ mr: 0.5 }}
              >
                <ShowChartIcon />
              </IconButton>
            </Tooltip>
          )}
          
          {accountControlsVisible && (
            <NotificationBell />
          )}
          
          {/* Settings Menu */}
          {accountControlsVisible && <Tooltip title="Settings">
            <IconButton 
              color="inherit" 
              onClick={handleSettingsMenuOpen}
              sx={{ mr: { xs: 0, sm: 2 } }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>}
          
          <Menu
            anchorEl={settingsMenuAnchor}
            open={accountControlsVisible && settingsMenuOpen}
            onClose={handleSettingsMenuClose}
            MenuListProps={{
              'aria-labelledby': 'settings-button',
            }}
          >
            <MenuItem onClick={handleProfileNavigate}>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleThemeToggle}>
              <ListItemIcon>
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </ListItemIcon>
              <ListItemText primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'} />
            </MenuItem>
            <MenuItem onClick={handleThemeSettings}>
              <ListItemIcon>
                <PaletteIcon />
              </ListItemIcon>
              <ListItemText primary="Theme Settings" />
            </MenuItem>
            <Divider />
            {runtime.isWeb() && <MenuItem onClick={handleTutorialOpen}>
              <ListItemIcon>
                <BookmarkBorderIcon />
              </ListItemIcon>
              <ListItemText primary="Bookmark Tutorial" />
            </MenuItem>}
            {runtime.isWeb() && <Divider />}
            <MenuItem onClick={handleLogoutFromMenu}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      {accountControlsVisible && (
        <Box sx={{ 
          padding: '1rem',
        }}>
          <UnitDropdown />
        </Box>
      )}
      
      <Dialog open={runtime.isNative() && logoutOpen} onClose={() => { if (!loggingOut) setLogoutOpen(false); }}
        aria-labelledby="logout-title" aria-describedby="logout-description">
        <DialogTitle id="logout-title">Log out of this device?</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-description">
            {runtime.isNative()
              ? 'You will no longer receive push notifications from this app on this device. Saved biometric login will be removed, and you will need your username and password to sign in again. Other devices will stay signed in.'
              : 'Are you sure you want to log out? You will need to sign in again to access your account.'}
          </DialogContentText>
          {logoutError && <Alert severity="error" sx={{ mt: 2 }}>{logoutError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={loggingOut} onClick={() => setLogoutOpen(false)}>Cancel</Button>
          <Button disabled={loggingOut} onClick={handleLogout} color="error" variant="contained">
            {loggingOut ? 'Logging out…' : 'Log out'}
          </Button>
        </DialogActions>
      </Dialog>
      <BookmarkTutorial 
        open={accountControlsVisible && tutorialOpen}
        onClose={() => setTutorialOpen(false)} 
      />
      
      <ThemeSettings
        open={accountControlsVisible && themeSettingsOpen}
        onClose={() => setThemeSettingsOpen(false)}
      />
    </>
  );
};

export default Header;
